import { TalkingAvatarToggleComponent } from './talking-avatar-toggle/talking-avatar-toggle.component';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule,Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { VoiceEmotion } from '@heygen/streaming-avatar';
import { AutosizeModule } from 'ngx-autosize';
import { Subscription } from 'rxjs';

import { TLanguage } from '@core/interfaces/TBot';
import { TBotItem, UiConfiguration } from '@core/interfaces/TBotItem';
import { TSourceItem } from '@core/interfaces/TSourceItem';

import { TalkingAvatarService } from '../../../../../routes/bots/talking-avatar/talking-avatar.service';
import { ConversationDataService } from '../../../services/conversation-data.service';
import { IInputTransferData } from '../../../types/dtos';
import { BotsService } from '@shared/services/bots.service';
import { CommonService } from '@shared/services/common.service';
import { KeywordsService, SelectedKeyword } from '@shared/services/keywords.service';
import { LocalSourcesService } from '@shared/services/local-sources.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { MultiSummaryService } from '@shared/services/multi-summary.service';

import { ConversationFiltersComponent } from '../conversation-filters/conversation-filters.component';
import { CustomButtonComponent } from '../message-controls/custom-button/custom-button.component';
import { PromptHintsDropdownComponent } from '../prompt-hints-dropdown/prompt-hints-dropdown.component';
import { PromptHintsMenuComponent } from '../prompt-hints-menu/prompt-hints-menu.component';
import { SourceSearchModalComponent } from '../source-search-modal/source-search-modal.component';
import { ConversationDndComponent } from './conversation-dnd/conversation-dnd.component';
import { InputFilePreviewComponent } from './input-file-preview/input-file-preview.component';
import { SpeechRecognitionComponent } from './speech-recognition/speech-recognition.component';

type FileErrorType = 'size' | 'ext' | 'maxInput';

interface ConversationFilters {
  _id?: string | null;
  language?: TLanguage | null;
  countries?: string[] | null;
  start?: string | null;
  end?: string | null;
}

export type ConversationInputOptions = {
  allowAttachFile?: boolean;
  isExtended?: boolean;
  filterButton?: boolean;
  promptHints?: string[];
};

@Component({
  selector: 'app-conversation-input',
  templateUrl: './conversation-input.component.html',
  styleUrls: ['./conversation-input.component.scss'],
  standalone: true,
  imports: [
    ConversationFiltersComponent,
    ReactiveFormsModule,
    InputFilePreviewComponent,
    MatIcon,
    AutosizeModule,
    ConversationDndComponent,
    CustomButtonComponent,
    PromptHintsMenuComponent,
    TalkingAvatarToggleComponent,
    SpeechRecognitionComponent,
    PromptHintsDropdownComponent,
  ],
})
export class ConversationInputComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isWriting = false;
  @Input() conversationStarted = false;
  @Input() options: ConversationInputOptions = {
    allowAttachFile: true,
    isExtended: false,
    filterButton: false,
    promptHints: [],
  };
  @Input() uiConfiguration?: UiConfiguration;

  @Output() messageSubmitted = new EventEmitter<IInputTransferData>();
  @Output() writingAbort = new EventEmitter();
  public messagesForm = this._fb.nonNullable.group({
    message: ['', [Validators.required, Validators.minLength(1)]],
  });
  protected filtersAreOpened = false;
  protected selectedSources: TSourceItem[] = [];
  protected defaultPlaceholder = 'Ask anything...';
  protected placeholder = this.defaultPlaceholder;
  protected isDragging = false;
  private dragCounter = 0;
  protected fileValidationParameters = {
    allowedExt: [
      'pdf',
      'txt',
      'csv',
      'docx',
      'pptx',
      'rtf',
      'html',
      'css',
      'png',
      'jpg',
      'xlsx',
      'xls',
    ],
    maxInputFiles: 5,
    maxFileSizeMB: 100,
  };
  protected errorsDictionary: { [key in FileErrorType]: string } = {
    size: `file must be under ${this.fileValidationParameters.maxFileSizeMB}MB`,
    ext: 'unsupported extension',
    maxInput: 'maximum number of files reached',
  };
  protected preventAttachFile = false;
  protected isInputFocused = false;
  protected files: File[] = [];
  private _bot: TBotItem | null = null;
  protected hasSummary = false;
  private _subs: Subscription[] = [];
  private _filesErrors: Set<FileErrorType> = new Set<FileErrorType>();
  private readonly SHARED_SOURCES_KEY = 'global';
  private hasSubmittedFiles = false;
  protected currentFilters: ConversationFilters = {};
  // Aggregated prompt hints from all dashboard bots for use when no specific bot is set
  protected aggregatedPromptHints: string[] = [];
  protected selectedHintIndex = -1;
  protected selectedKeywords: SelectedKeyword[] = [];
  protected activeKeywords: SelectedKeyword[] = [];

  constructor(
    private _fb: FormBuilder,
    private _commonService: CommonService,
    private _multiSummaryService: MultiSummaryService,
    private _localSourcesService: LocalSourcesService,
    private _dialog: MatDialog,
    private _botsService: BotsService,
    private _conversationDataService: ConversationDataService,
    protected _talkingAvatarService: TalkingAvatarService,
    private _keywordsService: KeywordsService,
    private _metadataService: MetadataService,
    private _cdr: ChangeDetectorRef
  ) {
    this._multiSummaryService
      .getVisibilityState()
      .subscribe(isVisible => (this.hasSummary = isVisible));
  }

  get bot(): TBotItem | null {
    return this._bot;
  }

  @Input() set bot(value: TBotItem | null) {
    this._bot = value;
  }

  @Input() set message(msg: string) {
    this.updateMessage(msg);
  }

  get filesLeftQty(): number {
    return this.fileValidationParameters.maxInputFiles - this.files.length;
  }

  ngOnInit() {
    this._filesErrors.clear();
    this.dragCounter = 0;
    this._subs.push(
      this._localSourcesService.getItems(this.SHARED_SOURCES_KEY).subscribe(sources => {
        this.selectedSources = sources;
      })
    );

    // Subscribe to all bots to aggregate prompt hints for dashboard use
    this._subs.push(
      this._botsService.getBotsObservable().subscribe(bots => {
        this.aggregatePromptHints(bots);
      })
    );

    // Load selected keywords from metadata
    this.loadSelectedKeywords();

    // Subscribe to conversation changes to load keywords from latest message
    this._subs.push(
      this._conversationDataService.conversation$.subscribe(conversation => {
        // Only load keywords when conversation has an ID (loaded from history) and has messages
        if (conversation?.id && conversation.messages && conversation.messages.length > 0) {
          // Get keywords from the latest user message
          const lastUserMessage = conversation.getLastMessageByAuthor('user');
          if (lastUserMessage && lastUserMessage.keywords && lastUserMessage.keywords.length > 0) {
            // Convert string[] to SelectedKeyword[] and set as active
            this.activeKeywords = lastUserMessage.keywords.map((keyword: string) => ({
              keyword,
              displayText: keyword,
            }));
            this.setPlaceholder();
          } else if (!lastUserMessage?.keywords || lastUserMessage.keywords.length === 0) {
            // Clear active keywords if the loaded conversation has no keywords
            this.activeKeywords = [];
            this.setPlaceholder();
          }
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.initialSubmissionHadFiles &&
      changes.initialSubmissionHadFiles.currentValue === true &&
      !changes.initialSubmissionHadFiles.firstChange
    ) {
      this.hasSubmittedFiles = true;
    }
  }

  ngOnDestroy() {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  onFileSelected(event: any) {
    this.handleFileInput(event.target.files);
  }

  handleFileInput(files: File[]) {
    // Files are now allowed at any point in the conversation

    this._filesErrors.clear();

    if (this.files.length >= this.fileValidationParameters.maxInputFiles) {
      this._filesErrors.add('maxInput');
      this.showErrors();
      this.isDragging = false;
      this.dragCounter = 0;
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (this.validateFile(files[i])) {
        const file = files[i];
        if (file) {
          this.files.push(file);
        }
      }
      if (this.filesLeftQty === 0) {
        if (files.length - i - 1 > 0) {
          this._filesErrors.add('maxInput');
        }
        break;
      }
    }
    this.showErrors();
    this.isDragging = false;
    this.dragCounter = 0;
  }

  submit() {
    if (this.isWriting || this.messagesForm.invalid) {
      return;
    }
    if (this.files.length > 0) {
      this.hasSubmittedFiles = true;
    }
    const messageData = {
      message: this.messagesForm.controls.message.value,
      files: this.files,
      sourceIds: this.selectedSources.map(source => source._id),
      project_id: this.currentFilters._id,
      language: this.currentFilters?.language,
      language_name: this.currentFilters?.language?.name,
      language_code: this.currentFilters?.language?.code,
      dateStart: this.currentFilters?.start,
      dateEnd: this.currentFilters?.end,
      countries: this.currentFilters?.countries,
      keywords: this.activeKeywords.length > 0 ? this.activeKeywords.map(k => k.keyword) : [],
    };

    this.messageSubmitted.emit(messageData);
    this.files = [];
    this.setPlaceholder();
    this.updateMessage('');
    // Don't clear activeKeywords - they persist throughout the conversation
    // User can manually deselect them if needed
    if (this.filtersAreOpened) {
      this.toggleFilters(false);
    }
  }

  removeFile(i: number) {
    this.files.splice(i, 1);
  }

  setPlaceholder(placeholder: string | null = null) {
    if (placeholder) {
      this.placeholder = placeholder;
    } else if (this.activeKeywords.length > 0) {
      const keywordsText = this.activeKeywords.map(k => k.displayText).join(', ');
      this.placeholder = `Ask about "${keywordsText}"...`;
    } else {
      this.placeholder = this.defaultPlaceholder;
    }
  }

  toggleFilters(force?: boolean) {
    this.filtersAreOpened = force ?? !this.filtersAreOpened;
  }

  protected get shouldShowFilters(): boolean {
    return !!this.options.filterButton;
  }

  openSourceSearch(): void {
    const dialogRef = this._dialog.open(SourceSearchModalComponent);
    dialogRef.componentInstance.botId = this.SHARED_SOURCES_KEY;
    dialogRef.componentInstance.sourceSelected.subscribe((source: TSourceItem) => {
      this._localSourcesService.addItem(this.SHARED_SOURCES_KEY, source);
      dialogRef.close();
    });
  }

  removeSource(source: TSourceItem): void {
    this._localSourcesService.removeItem(this.SHARED_SOURCES_KEY, source);
  }

  handleFilterChange(filters: ConversationFilters): void {
    this.currentFilters = filters;
  }

  protected updateMessage(msg: string | null) {
    if (msg === null) {
      this.messagesForm.controls.message.reset();
    } else {
      this.messagesForm.controls.message.patchValue(msg);
    }
  }

  protected validateFile(file: File) {
    const fileExt = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2);
    if (!this.fileValidationParameters.allowedExt.includes(fileExt)) {
      this._filesErrors.add('ext');
      return false;
    }
    if (file.size / 1024 > this.fileValidationParameters.maxFileSizeMB * 1024) {
      this._filesErrors.add('size');
      return false;
    }
    return true;
  }

  protected showErrors() {
    if (this._filesErrors.size) {
      let error = 'Some files were not added due to errors: ';
      error += Array.from(this._filesErrors)
        .map(type => this.errorsDictionary[type])
        .join(', ')
        .concat('.');
      this._commonService.openErrorSnackBar(error);
    }
  }

  handleDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter++;
    this.isDragging = true;
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    // Allow file drops throughout the conversation
  }

  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.isDragging = false;
    }
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    // Reset drag state when files are dropped
    this.isDragging = false;
    this.dragCounter = 0;
  }

  areFilesAllowedToAttach(): boolean {
    // Allow file attachments throughout the conversation
    return this.options.allowAttachFile!;
  }

  get hasPromptHints(): boolean {
    const optionsHints = this.options.promptHints && this.options.promptHints.length > 0;
    const aggregatedHints = this.aggregatedPromptHints.length > 0;
    return optionsHints || aggregatedHints;
  }

  get shouldShowPromptHintsDropdown(): boolean {
    // Only show dropdown on dashboard (when no specific bot is set) and conversation hasn't started
    return (
      this.hasPromptHints &&
      !this.isWriting &&
      this.messagesForm.get('message')?.value === '' &&
      !this.bot &&
      !this.conversationStarted
    );
  }

  protected getEffectivePromptHints(): string[] {
    // If we have specific prompt hints from options (bot-specific), use those
    if (this.options.promptHints && this.options.promptHints.length > 0) {
      return this.options.promptHints;
    }
    // Otherwise, use aggregated hints from all bots (dashboard scenario)
    return this.aggregatedPromptHints;
  }

  protected selectPromptHint(hint: string): void {
    this.updateMessage(hint);
    this.selectedHintIndex = -1;
    // Keep focus on input to allow immediate typing
  }

  protected onInputFocus(): void {
    this.isInputFocused = true;
    this.selectedHintIndex = -1;
  }

  protected onInputBlur(): void {
    // Delay hiding to allow click events on dropdown items
    setTimeout(() => {
      this.isInputFocused = false;
      this.selectedHintIndex = -1;
    }, 150);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    // Handle special keys for dropdown navigation (only on dashboard)
    if (this.shouldShowPromptHintsDropdown && this.isInputFocused && !this.bot) {
      const hints = this.getEffectivePromptHints();

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedHintIndex = Math.min(this.selectedHintIndex + 1, hints.length - 1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedHintIndex = Math.max(this.selectedHintIndex - 1, -1);
          return;
        case 'Enter':
          if (this.selectedHintIndex >= 0 && this.selectedHintIndex < hints.length) {
            event.preventDefault();
            this.selectPromptHint(hints[this.selectedHintIndex]);
            return;
          }
          // Handle regular enter behavior
          if (event.shiftKey) {
            // Allow shift+enter for new line
            return;
          } else {
            // Submit on enter
            event.preventDefault();
            this.submit();
            return;
          }
        case 'Escape':
          event.preventDefault();
          this.isInputFocused = false;
          this.selectedHintIndex = -1;
          return;
      }
    } else {
      // Handle regular enter behavior when dropdown is not visible
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          // Allow shift+enter for new line
          return;
        } else {
          // Submit on enter
          event.preventDefault();
          this.submit();
          return;
        }
      }
    }
  }

  private aggregatePromptHints(bots: TBotItem[]): void {
    const allHints: string[] = [];

    bots.forEach(bot => {
      // Only include prompt hints from bots that are shown on dashboard
      if (bot.showOnDashboard && bot.promptHints && bot.promptHints.length > 0) {
        allHints.push(...bot.promptHints);
      }
    });

    // Remove duplicates, filter empty strings, and limit for performance
    const uniqueHints = [...new Set(allHints)]
      .filter(hint => hint && hint.trim().length > 0)
      .slice(0, 15); // Reduced to 15 for better UX

    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    // This ensures the update happens outside the current change detection cycle
    setTimeout(() => {
      this.aggregatedPromptHints = uniqueHints;
      this._cdr.markForCheck();
    }, 0);
  }

  protected hasAssistantMessages(): boolean {
    const conversation = this._conversationDataService.currentValue;
    return conversation?.messages?.some(msg => msg.author === 'assistant') || false;
  }

  protected isTalkingAvatarActive(): boolean {
    return !this._talkingAvatarService.isClosed;
  }

  protected readConversationAloud(): void {
    const conversation = this._conversationDataService.currentValue;
    if (!conversation?.messages) {
      return;
    }

    // Get only assistant messages
    const assistantMessages = conversation.messages.filter(msg => msg.author === 'assistant');

    if (assistantMessages.length === 0) {
      this._commonService.openErrorSnackBar('No assistant messages to read aloud.');
      return;
    }

    // Combine all assistant messages into one text
    let fullText = assistantMessages
      .map(message => {
        // Use the same method as export to get clean text
        return conversation.getMessageToString(message, false); // false = no references for speech
      })
      .join('\n\n'); // Join messages with double line break

    // HeyGen API has a 5000 character limit, so we need to truncate if necessary
    const MAX_TEXT_LENGTH = 4800; // Leave some buffer
    let wasTruncated = false;

    if (fullText.length > MAX_TEXT_LENGTH) {
      wasTruncated = true;
      // Try to get the most recent messages first
      const reversedMessages = [...assistantMessages].reverse();
      let truncatedText = '';

      for (const message of reversedMessages) {
        const messageText = conversation.getMessageToString(message, false);
        const testText = messageText + (truncatedText ? '\n\n' + truncatedText : '');

        if (testText.length <= MAX_TEXT_LENGTH) {
          truncatedText = testText;
        } else {
          // If even this message is too long, take part of it
          if (!truncatedText) {
            truncatedText = messageText.substring(0, MAX_TEXT_LENGTH - 3) + '...';
          }
          break;
        }
      }

      fullText = truncatedText;
    }

    // Notify user if content was truncated
    if (wasTruncated) {
      this._commonService.openSnackBar(
        'Note: Only the most recent part of the conversation will be read aloud due to length limits.'
      );
    }

    // Set up proper recording session for results
    const lastAssistantMessage = conversation.getLastMessageByAuthor('assistant');

    if (conversation.id && lastAssistantMessage?._id) {
      this._talkingAvatarService.setRecordingIds(conversation.id, lastAssistantMessage._id);
    }

    // Set up talking avatar with current bot if available
    if (this.bot) {
      this._talkingAvatarService
        .setSettings(this.bot)
        .then(() => {
          this._talkingAvatarService.sendQuestion(fullText).catch(error => {
            console.error('Failed to start reading conversation:', error);
            this._commonService.openErrorSnackBar(
              'Failed to start reading conversation. Please check your internet connection and try again.'
            );
          });
        })
        .catch(error => {
          console.error('Failed to configure talking avatar:', error);
          this._commonService.openErrorSnackBar(
            'Failed to configure talking avatar. Please try again.'
          );
        });
    } else {
      // Set a default config if none exists
      if (!this._talkingAvatarService.config) {
        this._talkingAvatarService.config = {
          isAvailable: true,
          avatarId: 'Judy_Doctor_Sitting2_public',
          voiceRate: 1,
          voiceEmotion: VoiceEmotion.BROADCASTER,
        };
      }
      this._talkingAvatarService.sendQuestion(fullText).catch(error => {
        console.error('Failed to start reading conversation:', error);
        this._commonService.openErrorSnackBar(
          'Failed to start reading conversation. Please check your internet connection and try again.'
        );
      });
    }
  }

  private loadSelectedKeywords(): void {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardKeywords).subscribe({
        next: metadata => {
          if (metadata.value) {
            try {
              this.selectedKeywords = JSON.parse(metadata.value) as SelectedKeyword[];
            } catch (error) {
              console.error('Error parsing selected keywords:', error);
              this.selectedKeywords = [];
            }
          } else {
            this.selectedKeywords = [];
          }
        },
        error: () => {
          this.selectedKeywords = [];
        },
      })
    );
  }

  protected onKeywordClick(keyword: SelectedKeyword): void {
    // Toggle the keyword active state
    const isActive = this.activeKeywords.some(k => k.keyword === keyword.keyword);

    if (isActive) {
      // If keyword is active, remove it from active keywords
      this.activeKeywords = this.activeKeywords.filter(k => k.keyword !== keyword.keyword);
    } else {
      // If keyword is not active, add it to active keywords
      this.activeKeywords.push(keyword);
    }

    // Update placeholder to reflect the active keywords
    this.setPlaceholder();
  }

  protected isKeywordActive(keyword: SelectedKeyword): boolean {
    return this.activeKeywords.some(k => k.keyword === keyword.keyword);
  }

  protected getKeywordsToDisplay(): SelectedKeyword[] {
    // In conversation view (with bot), show active keywords
    // On dashboard (no bot), show selected keywords
    if (this.bot && this.activeKeywords.length > 0) {
      return this.activeKeywords;
    }
    return this.selectedKeywords;
  }

  protected get shouldShowKeywords(): boolean {
    // Show keywords in two scenarios:
    // 1. Dashboard (no bot) - show selected keywords for new conversations
    // 2. Active conversation - show active keywords being used
    if (!this.bot && this.selectedKeywords.length > 0) {
      return true; // Dashboard view
    }
    if (this.bot && this.activeKeywords.length > 0) {
      return true; // Conversation view with active keywords
    }
    return false;
  }

  protected showAvatarDisclaimer(): boolean {
    // Show disclaimer when:
    // 1. Avatar is not closed (active)
    // 2. Avatar is working or sending (talking)
    // 3. We have a bot (avatar is only available with specific bots)
    if (!this.bot || this._talkingAvatarService.isClosed) {
      return false;
    }

    // Check if avatar is currently working/talking
    // We need to access the private status property through any casting for now
    const service = this._talkingAvatarService as any;
    const isWorking = service.status?.status === 'working' || service.isSending;

    return isWorking;
  }

  /**
   * Clear active keywords - call this when starting a new conversation
   */
  public clearActiveKeywords(): void {
    this.activeKeywords = [];
    this.setPlaceholder();
  }
}
